// ---------------------------------------------------------------------------
// V34 — THE OFFLINE QUEUE.
//
// D9: the mobile app works in a steel-framed stadium with no signal. A check-in
// tapped there has to survive the walk back out, the browser being killed, and
// the handset being restarted, which is why it goes to IndexedDB and not to a
// variable.
//
// ---------------------------------------------------------------------------
// WHAT THIS FILE MAY AND MAY NOT DECIDE
//
//   IT MAY NOT DECIDE WHICH CAPTURE WINS. The whole queue is sent in one
//   request and `app.sync_offline_batch` orders it. A client that resolved
//   conflicts would be a second copy of the rule, and the copy is the one that
//   is wrong the day the rule changes.
//
//   IT MAY NOT DEFAULT AN ACCURACY. A geofence needs an accuracy ceiling or it
//   always passes, and a fabricated 0 claims a perfect reading and passes every
//   fence ever drawn. `accuracy_metres` is whatever the browser's own
//   `GeolocationPosition` reported and is ABSENT when there was none — see
//   `positionFrom` below, which is the only place a position enters this queue.
//
//   IT RECORDS THE DEVICE'S TIME AND KNOWS IT IS A CLAIM. Two of them, in fact:
//   when the person tapped and when the record went into the queue. A device
//   clock is settable by its owner, so neither is treated as the time of
//   anything — the server admits an instant against bounds it can vouch for and
//   says which clock it used.
//
//   IT DOES NOT CLEAR THE QUEUE ON A 200. It clears exactly the captures the
//   server named in its reply. A queue emptied because the request succeeded
//   would drop anything the database declined to resolve, and the person would
//   never learn their check-in did not land.
//
// THE STORE IS INJECTED so the logic can be tested without a browser. That is
// not an abstraction for its own sake: the queue's behaviour on a partial reply
// is the part that matters and it is unreachable through a real IndexedDB in a
// unit test.
// ---------------------------------------------------------------------------

/** One check-in, as it sits on the handset. */
export type QueuedCapture = {
  /** Minted here. It is the row's primary key server-side too, so a sync whose
   *  answer was lost to a dropped connection replays to the same row. */
  id: string;
  assignment_id: string;
  /** An id from `/v1/reference/attendance-kinds`. Never a word spelled here. */
  kind: string;
  /** What the handset said when the person tapped. A claim, not a time. */
  device_reported_at: string;
  /** What the handset said when this went into the queue. Also a claim. */
  queued_at: string;
  idempotency_key: string;
  latitude?: number;
  longitude?: number;
  /** Absent when the reading had none. NEVER zero, and never invented. */
  accuracy_metres?: number;
};

/** What the server made of one capture. Mirrors `app.sync_outcome`. */
export type Resolution = {
  capture_id: string;
  outcome: string;
  outcome_label: string;
  clock_ruling: string | null;
  ruling_label: string | null;
  refusal_reason: string | null;
  disagreement_seconds: number | null;
};

/** The little of IndexedDB this needs, so a test can stand in for it. */
export type CaptureStore = {
  all(): Promise<QueuedCapture[]>;
  put(capture: QueuedCapture): Promise<void>;
  remove(ids: string[]): Promise<void>;
};

/**
 * Turn a browser position into the three fields a capture carries.
 *
 * ★ THE ONLY PLACE A POSITION ENTERS THE QUEUE, and the reason it is a function
 * rather than three lines at the call site: an accuracy that is missing must
 * stay missing. `GeolocationCoordinates.accuracy` is required by the standard,
 * but a shimmed or mocked position can arrive without one, and the tempting
 * fix — `?? 0` — is the defect this whole rule exists to prevent.
 */
export function positionFrom(
  position: { coords: { latitude: number; longitude: number; accuracy?: number } } | null,
): Pick<QueuedCapture, "latitude" | "longitude" | "accuracy_metres"> {
  if (position === null) return {};
  const { latitude, longitude, accuracy } = position.coords;
  if (typeof accuracy !== "number" || !Number.isFinite(accuracy)) {
    // NO ACCURACY MEANS NO POSITION. Sending the coordinates without it would
    // be refused by the database anyway, by name — but sending them and letting
    // the server decide is the honest behaviour, so they are sent and the
    // absence is preserved rather than papered over.
    return { latitude, longitude };
  }
  return { latitude, longitude, accuracy_metres: Math.round(accuracy) };
}

/** A key long enough for the database's own minimum, and unique per capture. */
function idempotencyKey(id: string): string {
  return `offline-${id}`;
}

/**
 * Put a check-in into the queue.
 *
 * `now` is injected rather than read, because a queue that reads the clock in
 * two places can stamp two different instants on one act.
 */
export async function enqueueCapture(
  store: CaptureStore,
  capture: {
    id: string;
    assignment_id: string;
    kind: string;
    position?: { coords: { latitude: number; longitude: number; accuracy?: number } } | null;
  },
  now: Date,
): Promise<QueuedCapture> {
  const at = now.toISOString();
  const queued: QueuedCapture = {
    id: capture.id,
    assignment_id: capture.assignment_id,
    kind: capture.kind,
    device_reported_at: at,
    queued_at: at,
    idempotency_key: idempotencyKey(capture.id),
    ...positionFrom(capture.position ?? null),
  };
  await store.put(queued);
  return queued;
}

export type DrainResult = {
  sent: number;
  resolutions: Resolution[];
  /** Still on the handset after this attempt. */
  remaining: number;
  /** Set when the request itself did not complete. The queue is untouched. */
  failed: string | null;
};

/**
 * Send the queue and clear exactly what came back.
 *
 * `post` is injected so this is testable and so nothing here knows about
 * cookies, CSRF headers or the API base — those belong to `api/offline.ts`.
 */
export async function drainQueue(
  store: CaptureStore,
  post: (captures: QueuedCapture[]) => Promise<{ ok: boolean; resolutions?: Resolution[]; message?: string }>,
): Promise<DrainResult> {
  const pending = await store.all();
  if (pending.length === 0) {
    return { sent: 0, resolutions: [], remaining: 0, failed: null };
  }

  const reply = await post(pending);
  if (!reply.ok || !Array.isArray(reply.resolutions)) {
    // ★ NOTHING IS CLEARED. Still underground, or the server refused the whole
    // batch: either way every capture is still the only record of somebody
    // turning up, and this is the branch where losing one would be silent.
    return {
      sent: pending.length,
      resolutions: [],
      remaining: pending.length,
      failed: reply.message ?? "The queue could not be sent.",
    };
  }

  // ★ CLEARED BY NAME, not by count and not by emptying the store. A reply
  // resolving four of five leaves the fifth exactly where it was.
  const resolved = new Set(reply.resolutions.map((r) => r.capture_id));
  const clearing = pending.filter((c) => resolved.has(c.id)).map((c) => c.id);
  await store.remove(clearing);

  return {
    sent: pending.length,
    resolutions: reply.resolutions,
    remaining: pending.length - clearing.length,
    failed: null,
  };
}

/** The captures a person still has to be told about after a drain. */
export function needsAttention(resolutions: Resolution[]): Resolution[] {
  // KEYED ON WHAT THE SERVER SAID, not on a list of outcome ids kept here. The
  // catalogue is `app.sync_outcome` and a second copy of it in the browser is
  // absolute rule 4 broken in the place it is hardest to see.
  return resolutions.filter((r) => r.refusal_reason !== null || (r.disagreement_seconds ?? 0) < 0);
}
